import {
  createConversation,
  deleteConversation,
  listConversations,
  type Conversation,
} from '@api/agent';
import { AddIcon, DeleteIcon } from '@assets/icons';
import { Button, IconButton } from '@components/button';
import { Group } from '@components/layout';
import { LoadingPanel } from '@components/modal';
import { DateToNowDisplay, Typography } from '@components/typography';
import { Stack, useTheme } from '@mui/material';
import posthog from 'posthog-js';
import { useCallback, useEffect, useState, type MouseEvent } from 'react';

type AgentChatConversationListProps = {
  token: string;
  organizationId: string;
  workspaceId?: string;
  /** Called when user selects or creates a conversation */
  onSelectConversation: (conversationId: string, title?: string) => void;
};

/**
 * Conversation list view for the agent chat panel.
 * Shows loading, error, and empty states.
 * Allows creating new conversations and deleting existing ones.
 */
export const AgentChatConversationList = ({
  token,
  organizationId,
  workspaceId,
  onSelectConversation,
}: AgentChatConversationListProps) => {
  const { palette } = useTheme();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /** Fetch conversations for the current organization */
  const fetchConversations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const convos = await listConversations(token, organizationId);
      setConversations(convos);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load conversations');
    } finally {
      setLoading(false);
    }
  }, [token, organizationId]);

  useEffect(() => {
    void fetchConversations();
  }, [fetchConversations]);

  /** Create a new conversation and navigate to it */
  const handleNewChat = async () => {
    try {
      const convo = await createConversation(token, { organizationId, workspaceId });
      posthog.capture('agent-chat:user_conversation_created', {
        organizationId,
        workspaceId,
        conversationId: convo.conversationId,
        source: 'list',
      });
      setConversations((prev) => [convo, ...prev]);
      onSelectConversation(convo.conversationId);
    } catch (err) {
      console.error('[AgentChatConversationList] Failed to create conversation:', err);
    }
  };

  /** Delete a conversation */
  const handleDelete = async (e: MouseEvent, conversationId: string) => {
    e.stopPropagation();
    try {
      await deleteConversation(token, conversationId);
      setConversations((prev) => prev.filter((c) => c.conversationId !== conversationId));
    } catch (err) {
      console.error('[AgentChatConversationList] Failed to delete conversation:', err);
    }
  };

  return (
    <Stack sx={{ height: '100%', overflow: 'hidden' }} data-testid="agent-chat-conversation-list">
      {/* Header bar */}
      <Group
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 4,
          py: 2,
          bgcolor: 'header.secondary',
          minHeight: 48,
          borderBottom: '1px solid',
          borderColor: 'background.default',
          color: 'grey.300',
        }}
      >
        <Typography variant="body1">Agent Chat</Typography>
        <IconButton
          Icon={AddIcon}
          size={20}
          onClick={handleNewChat}
          color={palette.grey[300]}
          tooltip="New chat"
          tooltipProps={{ variant: 'secondary' }}
          data-testid="agent-chat-new-btn"
        />
      </Group>

      {/* Content area */}
      <Stack sx={{ flex: 1, overflow: 'auto', p: 2, gap: 2 }}>
        {loading && <LoadingPanel height="100px" />}

        {!loading && error && (
          <Stack alignItems="center" gap={2} pt={4}>
            <Typography variant="body2" color="error">
              {error}
            </Typography>
            <Button size="small" variant="secondary" onClick={fetchConversations}>
              Retry
            </Button>
          </Stack>
        )}

        {!loading && !error && conversations.length === 0 && (
          <Stack
            alignItems="center"
            gap={2}
            sx={{
              backgroundColor: 'background.paperCard',
              padding: 4,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              cursor: 'pointer',
              color: 'foreground.paperCard.light',
              '&:hover': {
                color: 'foreground.paperCard.default',
                borderColor: 'foreground.paperCard.default',
              },
            }}
            role="button"
            onClick={handleNewChat}
            data-testid="agent-chat-new-btn-2"
          >
            <Typography variant="body2">No conversations yet</Typography>
            <Typography variant="caption1">Click here to start a new conversation</Typography>
          </Stack>
        )}

        {!loading &&
          !error &&
          conversations.map((convo) => (
            <Group
              key={convo.conversationId}
              alignItems="center"
              sx={{
                px: 2,
                py: 2,
                gap: 4,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'background.paper',
                color: 'foreground.paper.light',
                cursor: 'pointer',
                '&:hover': {
                  color: 'foreground.paper.default',
                  borderColor: 'foreground.paper.default',
                },
              }}
              onClick={() => {
                posthog.capture('agent-chat:user_conversation_selected', {
                  organizationId,
                  workspaceId,
                  conversationId: convo.conversationId,
                });
                onSelectConversation(convo.conversationId, convo.title);
              }}
              data-testid={`agent-chat-conversation-item-${convo.conversationId}`}
            >
              <Stack sx={{ flex: 1, overflow: 'hidden' }}>
                <Typography variant="body2" handleOverFlow>
                  {convo.title ?? convo.conversationId.slice(0, 8)}
                </Typography>
                <DateToNowDisplay
                  date={convo.updatedAt}
                  variant="caption2"
                  sx={{ color: 'foreground.paperCard.light' }}
                />
              </Stack>
              <IconButton
                Icon={DeleteIcon}
                variant="error"
                size={16}
                tooltip="Delete conversation"
                onClick={(e) => handleDelete(e, convo.conversationId)}
                data-testid={`agent-chat-delete-btn-${convo.conversationId}`}
              />
            </Group>
          ))}
      </Stack>
    </Stack>
  );
};
